<div class="min-h-screen bg-gradient-to-br from-base-100 to-base-200">
  <!-- Environment Indicator Badge -->
  @if (!isProduction) {
    <div
      class="fixed bottom-4 right-4 z-50 px-3 py-1 rounded-full text-white font-bold text-sm shadow-lg cursor-default select-none"
       [ngClass]="{
         'bg-blue-600': environmentName === 'DEV',
         'bg-orange-500': environmentName === 'QA',
         'bg-green-600': environmentName === 'PROD'
       }"
      [title]="'Environment: ' + environmentName"
      aria-hidden="true"
      data-testid="main-layout-env-badge">
      {{ environmentName }}
    </div>
  }

  <!-- Responsive layout with fixed sidebar -->
  <div class="flex min-h-screen">
    <!-- Sidebar - Fixed position on desktop, hidden on mobile -->
    <div class="hidden lg:block">
      <app-sidebar></app-sidebar>
    </div>

    <!-- Main content area with proper spacing -->
    <div class="main-content flex-1 min-w-0">
      <!-- Mobile navigation bar -->
      <nav class="mobile-nav navbar max-sm:flex-wrap max-sm:gap-2 lg:hidden bg-gradient-to-r from-primary/10 to-secondary/10 border-b border-primary/20" aria-label="Mobile navigation" data-testid="main-layout-mobile-nav">
        <div class="flex-none">
          <div class="dropdown">
            <div tabindex="0" role="button" class="btn btn-square btn-ghost hover:bg-primary/20" aria-label="Open navigation menu" aria-haspopup="true" data-testid="main-layout-mobile-menu-btn">
              <app-icon name="menu" size="md" class="text-primary"></app-icon>
            </div>
<!--
              KAN-567. TWO separate things here are load-bearing at 320px, one on each
              element. Each was mutated out individually against a real browser and the
              guard went red for both; nothing else here survived that test.

              1. `max-w-[calc(100vw-1rem)]` on the dropdown. `w-80` is a flat 320px and
                 the dropdown starts 8px in from the navbar padding, so at a 320px
                 viewport the OPEN menu ended at 328px - 8px past the screen. Removing
                 this cap: open menu scrollWidth 328 vs clientWidth 320.

              2. `w-full` on <app-sidebar>. The sidebar host is a flex ITEM of this
                 dropdown and, left to size itself, took its own min-content width -
                 measured 337.516px inside a 318px box. That mattered even with the
                 menu CLOSED, because a closed DaisyUI dropdown is `visibility:hidden`
                 but still fully laid out, so it still counts toward document width.
                 Result before the fix, on EVERY authenticated route: scrollWidth 338
                 vs clientWidth 320, scrollLeft reading back 18. Removing `w-full`:
                 scrollWidth 337 vs 320.

              `min-w-0` and `block` were in an earlier revision of this fix and have
              been REMOVED. They read as obviously necessary - the flex-item
              `min-width:auto` floor is the textbook cause here - but mutation testing
              showed the guard passes with each of them deleted, so neither could ever
              fail for the reason it claimed to exist. (`min-width:auto` resolves to the
              content-based minimum on the MAIN axis only, and this dropdown is a column
              flex container, so width is the cross axis.) A class that cannot be shown
              to matter is not defence in depth, it is a false explanation sitting next
              to a real one.

              `overflow-x: hidden` is NOT an acceptable substitute for either. It
              satisfies a scrollWidth assertion while pushing the nav links and the
              logout button off screen, and that photographs exactly like a correct
              layout. Guarded by e2e/tests/app-shell-horizontal-overflow.spec.ts, which
              opens the menu at 320px and asserts every control is fully on screen,
              wide enough to use, and not internally truncated.
            -->
            <div tabindex="0" class="dropdown-content menu bg-base-200 border border-base-300 rounded-box z-[1] w-80 max-w-[calc(100vw-1rem)] p-0 shadow-lg" role="menu">
              <app-sidebar class="w-full"></app-sidebar>
            </div>
          </div>
        </div>
        <!--
          min-w-0 is load-bearing, not cosmetic. A flex item defaults to
          min-width:auto, which floors it at its own min-content width - so this
          brand block refused to shrink below ~208px despite flex-1, and the nav
          inherited a hard min-content floor that made every authenticated route
          scroll sideways on narrow viewports. min-w-0 removes the floor. Guarded by
          e2e/tests/app-shell-horizontal-overflow.spec.ts.

          KAN-624: the label is shown WHOLE OR NOT AT ALL. It used to `truncate`, and
          because this block is `flex-1` (basis 0) it only ever gets the width the
          icon cluster leaves over - so a phone showed a single stray "C" at 430px
          and "Cours..." at 480px. `brand-slot` is a size-query container and
          `brand-label` hides itself when the slot is narrower than the word; see
          main-layout.component.scss for the threshold and why it is not tighter.
        -->
        <div class="brand-slot flex-1 min-w-0 px-2 mx-2">
          <span class="brand-label text-lg font-bold text-primary whitespace-nowrap block" data-testid="main-layout-brand">CourseBuilder</span>
        </div>
        <!--
          The cluster may shrink (min-w-0, no flex-none) so it cannot impose a
          min-content floor on the nav. The icon controls keep their intrinsic size via
          flex-shrink-0; the org switcher is deliberately the ONE control that gives
          ground when the nav runs out of room.

          NO max-width cap is set, and that is deliberate. An earlier revision added a
          9rem cap as a "named decision"; independent code review (helper 216) showed the
          assertion guarding it was non-diagnostic, and measuring with the cap ABSENT
          showed the cap was entirely REDUNDANT: flex-shrink alone lands the switcher at
          144px at a 360px viewport (natural width 243px) with ZERO page overflow - the
          same width the cap was pinning. The cap was removed rather than the assertion
          repaired. Shrinking is safe because this copy WRAPS the org name rather than
          cutting it (KAN-624, `nameOverflow="wrap"`), so the whole name plus the role
          badge stay readable - an instructor must always be able to tell which org they
          are operating in. Asserted by e2e/tests/app-shell-horizontal-overflow.spec.ts.
        -->
        <!--
          KAN-567, second half. `flex-shrink` alone was NOT enough for a
          MULTI-organization user, and single-org accounts hid that completely:
          at 320px this cluster has ~104px to give the switcher after the
          hamburger, brand, bell and help button take their intrinsic widths, and
          the multi-org toggle's min-content width is 218.5px. There is no spare
          width anywhere in this row to reclaim, so the switcher is allowed to
          take a ROW OF ITS OWN when it cannot fit beside the icon buttons -
          `flex-wrap` on the cluster, `grow basis-44` on the switcher.

          `order-last` is load-bearing and is NOT cosmetic: without it the help
          button is the control that wraps, the nav becomes THREE rows and grows
          to 209px tall at 320px. With it the switcher is the one that drops and
          the nav stays at two rows (153px at 320/360, unchanged 97px at 414+).

          From `sm` (640px) up the wrap point is decided by whether an 11rem basis
          can sit beside the bell and the help button. That is independent of how
          long the active organization's NAME is - a `max-[414px]:` rule would have
          been pinned to one fixture's name length (the overflow measured 407px wide
          for "Default Organization" and 335px for "Kyle's Org" on the same 320px
          screen), so any name-derived breakpoint is right for one account and wrong
          for the next.

          KAN-624: below `sm` that basis was the problem. From ~392px it fits beside
          the icons, so the picker went BACK onto the top row and was squeezed to
          47-85px of name ("Defa...", "Default ..."). So below `sm` the picker
          ALWAYS takes a full-width row of its own: `max-sm:contents` dissolves this
          cluster so the icons and the picker become items of the nav itself (which
          wraps, `max-sm:flex-wrap`), and `max-sm:basis-full` puts the picker on the
          second row at the full nav width. That breakpoint does not depend on the
          name - the picker is on its own row at every phone width, for every
          account - so it is not the name-pinned rule rejected above.
          `order-last` still decides which item drops: within the nav below `sm`,
          and within this cluster from `sm` up.

          `menuAlign="end"` right-anchors the workspace menu for THIS copy only, and
          `nameOverflow="wrap"` shows the whole org name, wrapping if it must. The
          sidebar copies keep both defaults; see the component's own comments.
        -->
        <div class="max-sm:contents flex flex-wrap justify-end flex-shrink min-w-0 items-center gap-2">
          <app-global-upload-status class="flex-shrink-0"></app-global-upload-status>
          <app-notification-bell class="flex-shrink-0"></app-notification-bell>
          <app-organization-switcher
            class="min-w-0 grow basis-44 order-last max-sm:basis-full"
            menuAlign="end"
            nameOverflow="wrap"
            data-testid="main-layout-org-switcher-slot"></app-organization-switcher>
          <app-help-button class="flex-shrink-0"></app-help-button>
        </div>
      </nav>

      <!-- Page content -->
      <main class="p-4 lg:p-8" role="main" data-testid="main-layout-content">
        <router-outlet></router-outlet>
      </main>
    </div>
  </div>
</div>
